import thumbnailsModule from "../thumbnailsModule";
import { ThumbnailMetrics } from "Roblox";

function thumbnailService($q, $log, httpService, thumbnailConstants, batchRequestsService, batchRequestsConstants) {
    "ngInject";

    var thumbnailRequesters = {};
    var thumbnailRequesterProperties = {
        getFailureCooldown: batchRequestsService.cooldowns.createExponentialBackoffCooldown(thumbnailConstants.cooldown.minCooldown, thumbnailConstants.cooldown.maxCooldown),
        maxRetryAttempts: thumbnailConstants.cooldown.maxRetryAttempts,
        batchSize: thumbnailConstants.requestProperties.batchSize
    };
    var thumbnailPendingTracker = {};

    var idSerializer = function (id) {
        return id.toString();
    };

    var thumbnailCache = {};

    var getThumbnailCacheKey = function (thumbnailType, thumbnailTargetId, requestedSize) {
        return "thumbnail_type:" + thumbnailType + "_targetId:" + thumbnailTargetId + "_requestedSize:" + requestedSize;
    }
    
    var getUrlConfig = function (thumbnailType) {
        var urlConfig = {};

        switch (thumbnailType) {
            case thumbnailConstants.thumbnailTypes.assetThumbnail:
                urlConfig.url = thumbnailConstants.urls.getAssetThumbnails;
                break;
            case thumbnailConstants.thumbnailTypes.avatarHeadshot:
                urlConfig.url = thumbnailConstants.urls.getAvatarHeadshots;
                break;
            case thumbnailConstants.thumbnailTypes.avatar:
                urlConfig.url = thumbnailConstants.urls.getAvatar;
                break;
            case thumbnailConstants.thumbnailTypes.bundleThumbnail:
                urlConfig.url = thumbnailConstants.urls.getBundleThumbnails;
                break;
            case thumbnailConstants.thumbnailTypes.gameIcon:
                urlConfig.url = thumbnailConstants.urls.getGameIcons;
                break;
            case thumbnailConstants.thumbnailTypes.groupIcon:
                urlConfig.url = thumbnailConstants.urls.getGroupIcons;
                break;
            default:
                $log.debug("Missing urlConfig implementation for thumbnail type: " + thumbnailType);
                return null;
        }

        return urlConfig;
    };

    var getRequestParameters = function (thumbnailType, ids, size) {
        var requestParameters = {
            size: size,
            format: thumbnailConstants.formats.png
        };

        switch (thumbnailType) {
            case thumbnailConstants.thumbnailTypes.assetThumbnail:
                requestParameters.assetIds = ids.join(",");
                break;
            case thumbnailConstants.thumbnailTypes.avatarHeadshot:
            case thumbnailConstants.thumbnailTypes.avatar:
                requestParameters.userIds = ids.join(",");
                break;
            case thumbnailConstants.thumbnailTypes.bundleThumbnail:
                requestParameters.bundleIds = ids.join(",");
                break;
            case thumbnailConstants.thumbnailTypes.gameIcon:
                requestParameters.universeIds = ids.join(",");
                break;
            case thumbnailConstants.thumbnailTypes.groupIcon:
                requestParameters.groupIds = ids.join(",");
                break;
            default:
                $log.debug("Missing request parameters implementation for thumbnail type: " + thumbnailType);
                return null;
        }

        return requestParameters;
    };

    var getThumbnailRequester = function (thumbnailType, size) {
        var key = thumbnailType + ":" + size;

        if (thumbnailRequesters.hasOwnProperty(key)) {
            return thumbnailRequesters[key];
        }
        
        var processor = batchRequestsService.createRequestProcessor(function (ids) {
            var urlConfig = getUrlConfig(thumbnailType);
            var requestParameters = getRequestParameters(thumbnailType, ids, size);

            return $q(function (resolve, reject) {
                if (!urlConfig || !requestParameters) {
                    reject(batchRequestsConstants.errors.unretriableFailure);
                    return;
                }

                var requestStartTime = new Date().getTime();
                httpService.httpGet(urlConfig, requestParameters).then(function (responseBody) {
                    var results = {};

                    responseBody.data.forEach(function (item) {
                        var key = idSerializer(item.targetId);
                        results[key] = item;
                    });

                    ids.forEach(function (id) {
                        var key = idSerializer(id);

                        if (results.hasOwnProperty(key) && results[key]) {
                            // Removing it from the results we resolve with will make sure it gets retried.
                            if (results[key].state === thumbnailConstants.thumbnailStates.pending) {
                                delete results[key];
                                if (!thumbnailPendingTracker[key]) {
                                    thumbnailPendingTracker[key] = { startTime: requestStartTime };
                                }
                            }
                            else if (results[key].state === thumbnailConstants.thumbnailStates.complete &&
                                    thumbnailPendingTracker[key]) {
                                //call the logging function if this item was previously pending
                                var requestCompleteTime = new Date().getTime();
                                ThumbnailMetrics && ThumbnailMetrics.logFinalThumbnailTime(requestCompleteTime - thumbnailPendingTracker[key].startTime);
                            }
                        } else {
                            results[key] = {
                                targetId: id,
                                state: thumbnailConstants.thumbnailStates.error
                            };
                        }
                    });

                    resolve(results);
                }, function () {
                    // No success cases this time, all will be retried.
                    resolve({});
                });
            });
        }, idSerializer, thumbnailRequesterProperties);

        thumbnailRequesters[key] = processor;
        return processor;
    };

    var getRequestSize = function (size) {
        if (size) {
            if (thumbnailConstants.sizeMap.hasOwnProperty(size)) {
                return thumbnailConstants.sizeMap[size];
            } else {
                $log.debug("Invalid size for thumbnail: " + size + " (medium will be used.)");
            }
        }

        return thumbnailConstants.sizeMap.medium;
    };

    var getThumbnailImage = function (thumbnailType, targetId, size) {
        var thumbnailCacheKey = getThumbnailCacheKey(thumbnailType, targetId, size);

        if (!(thumbnailCacheKey in thumbnailCache)) {
            var batchRequester = getThumbnailRequester(thumbnailType, size);
            var result = batchRequester
                .queueItem(targetId)
                .catch(function (data) {
                    ThumbnailMetrics && ThumbnailMetrics.logThumbnailTimeout();
                    //chain the rejection so that other listeners get triggered.
                    return $q.reject(data);
                });
            thumbnailCache[thumbnailCacheKey] = result;
        }

        return thumbnailCache[thumbnailCacheKey];
    };

    return {
        getRequestSize: getRequestSize,
        getThumbnailImage: getThumbnailImage
    };
}

thumbnailsModule.factory("thumbnailService", thumbnailService);

export default thumbnailService;
